import type { MarketLine } from "./market-source";
import { mapPrematchEvent } from "./adapter";

// OPEN 3 2026-10-06: 41310565 Насину–Рева measured in Brave was already on
// the live board, whose feed carries no EHT/EAT; retain only store English names.
export function openEventNamesFor(
  openId: string | null,
  raw: Record<string, unknown> | null,
): Record<string, unknown> | undefined {
  if (!openId || !raw || typeof raw !== "object" || String(raw.Id ?? "") !== openId) return undefined;
  const home_en = typeof raw.EHT === "string" ? raw.EHT.trim() : "";
  const away_en = typeof raw.EAT === "string" ? raw.EAT.trim() : "";
  if (!home_en || !away_en) return undefined;
  const out: Record<string, unknown> = { id: openId, home_en, away_en };
  const sport_en = typeof raw.ESN === "string" ? raw.ESN.trim() : "";
  const league_en = typeof raw.ECN === "string" ? raw.ECN.trim() : "";
  if (sport_en) out.sport_en = sport_en;
  if (league_en) out.league_en = league_en;
  return out;
}

/** ⭐ THE OPEN PRE-MATCH FIXTURE'S LADDER.
 *
 * A fixture the visitor has open but which is NOT on the live board is
 * resolved as `contextOnlyId`. Its ladder was never posted: the poll body only
 * ever attached lines to entries of `live_matches`, so for a pre-match open
 * match the chooser saw the moneyline and nothing else.
 *
 * Pure by design — the poll loop has no test harness, so the decision lives
 * here where it can be driven directly. Cache-only, like the live ladders: an
 * id we have not primed yet is simply absent, which the backend reads as no
 * opinion rather than as an empty board.
 */
/** The last ladder posted per fixture. ⛔ parikara 2026-09-09 22:08 UTC: two
 * polls two seconds apart on the same pre-match page — the one whose cache read
 * came back empty carried NO open-match card, the one with the ladder pinned it
 * first, and the deck is rebuilt from whichever poll landed last. The catalog's
 * ladder cache goes empty between primes; the fixture on screen does not. */
const lastPosted = new Map<string, MarketLine[]>();

export function openEventMarketsFor(
  contextOnlyId: string | null,
  marketsFor: (id: string) => MarketLine[],
  subFor: (id: string) => MarketLine[] = () => [],
): { event_id: string; lines: MarketLine[] } | undefined {
  if (!contextOnlyId) return undefined;
  const fresh = marketsFor(contextOnlyId);
  if (Array.isArray(fresh) && fresh.length > 0) {
    // ⛔⛔ THE OPEN PRE-MATCH FIXTURE IS THE SAME-MATCH ACCA'S PRIMARY SURFACE,
    // and it does NOT go through `postedLadder` — that path serves
    // `live_matches` only, and this fixture is by definition not on the live
    // board. Merging corners/cards only there meant supply and scoring never
    // coincided: the lines arrived for live fixtures, while `prematch_reads`
    // fires for this one. The feature reached nobody.
    // (External review f630c346, 2026-09-20 — it found this, not a test.)
    // ⛔ `scored_lines` IS THE LADDER THE SERVER FALLS BACK TO when the primed one
  // (`open_event_markets`) is absent — production 2026-10-02 14:05–14:34 UTC: it
  // answered 11 of 50 open-match requests and never carried a corners/cards
  // line, because only the primed ladder had them appended. Same rule as there:
  // sub-event lines never summon a ladder the store did not give.
  const sub = subFor(contextOnlyId);
    const lines = sub.length ? fresh.concat(sub) : fresh;
    lastPosted.set(contextOnlyId, lines);
    return { event_id: contextOnlyId, lines };
  }
  // ⛔ Sub-event lines never SUMMON a ladder — same rule as `postedLadder`. A
  // fixture whose own ladder we have not primed still posts nothing.
  const kept = lastPosted.get(contextOnlyId);
  return kept ? { event_id: contextOnlyId, lines: kept } : undefined;
}

/** ⭐ THE OPEN FIXTURE ITSELF, when no board this client holds describes it.
 *
 * parikara 2026-09-09 22:13 UTC: Aston Villa–Forest open, 35h out — beyond the
 * pre-match board's 24h horizon on BOTH sides, so no board entry existed, the
 * server had nothing to pin, and the deck opened on tennis. The app's store
 * holds the event (names, league, sport, kick-off); the primed ladder holds its
 * prices. Together they are a board entry, posted under `page_context.open_event`
 * so the server can build the card for the fixture on screen from the poll alone.
 *
 * The 1X2 (Digitain StakeType 1; 702 for the 2-way moneyline) is the only
 * market attached — it is what `_digitain_context_candidate` prices the side
 * from; the whole ladder still travels in `open_event_markets`. Remembered per
 * fixture like the ladder, for the same reason. */
const lastEntry = new Map<string, Record<string, unknown>>();

export function openEventEntryFor(
  contextOnlyId: string | null,
  rawEvent: Record<string, unknown> | null,
  marketsFor: (id: string) => MarketLine[],
  subFor: (id: string) => MarketLine[] = () => [],
): Record<string, unknown> | undefined {
  if (!contextOnlyId) return undefined;
  const base = rawEvent && String(rawEvent.Id ?? "") === contextOnlyId ? mapPrematchEvent(rawEvent) : null;
  if (!base) return lastEntry.get(contextOnlyId);
  const sub = subFor(contextOnlyId);
  const scored_lines = base.scored_lines && base.scored_lines.length && sub.length
    ? base.scored_lines.concat(sub)
    : base.scored_lines;
  // The store event carries its own StakeTypes once the page has loaded them —
  // mapPrematchEvent already keeps the match-result market from those. That is
  // the freshest 1X2 there is; the ladder (same remembered copy the poll posts
  // under open_event_markets, so the two can never disagree) fills in when the
  // store has not loaded markets yet.
  // Read the store's own StakeTypes here rather than through the live-board
  // parser: that parser names a stake by `SN`, and the store's stakes carry the
  // side in `N` — parikara 2026-09-09 22:45 UTC posted "@2.25, @3.5, @3.25" with
  // no names, and a nameless 1X2 cannot tell the server which side is home.
  let markets: unknown[] = [];
  // ⛔ THE LADDER'S OWN MARKET NAME, for when the store's group has none.
  // MEASURED 2026-09-14 (QA, build 0c3f5263): 88 of 283 leg_markets entries were
  // match-winner (market_id 1) and **every one carried `market_name: ""`** — the
  // store's group arrives without `N` while the ladder names the same market
  // «Исход». Read through the same remembered copy the poll posts, so the two
  // can never disagree.
  const ladderName = (mid: number): string => {
    const ladder = openEventMarketsFor(contextOnlyId, marketsFor);
    const hit = (ladder ? ladder.lines : []).find((l) => l && l.market_id === mid);
    return hit ? String(hit.market_name ?? "") : "";
  };
  const groups = (rawEvent as { StakeTypes?: unknown }).StakeTypes;
  if (Array.isArray(groups)) {
    for (const g of groups) {
      const gg = g as { Id?: unknown; N?: unknown; SN?: unknown; Stakes?: unknown };
      const gid = Math.abs(Number(gg.Id));
      if (gid !== 1 && gid !== 702) continue;
      const stakes = Array.isArray(gg.Stakes) ? gg.Stakes : [];
      const selections = stakes
        .map((st) => st as { Id?: unknown; N?: unknown; SN?: unknown; F?: unknown; SC?: unknown })
        .filter((st) => st && st.Id != null && Number(st.F) > 1)
        .map((st) => ({
          stake_id: String(st.Id),
          name: String(st.N ?? st.SN ?? ""),
          odds: Math.round(Number(st.F) * 100) / 100,
          // ⛔ THE OUTCOME'S OWN CODE (Digitain `SC`), and it was missing from BOTH
          // markets this function builds. The backend copies it straight onto the
          // card's side (`app.py._winner_sides`), and without it a match-winner leg
          // is named by PRICE alone: a rotated stake id cannot be re-resolved
          // (`(market_id, code, line)` is the key), and a refusal matrix cannot tell
          // П1 from П2 — measured 2026-09-14, 18 of 88 match-winner legs had `code:
          // null`, so a third of the operator's refusals could not be attributed.
          code: st.SC == null || !Number.isFinite(Number(st.SC)) ? null : Number(st.SC),
        }));
      if (selections.length) {
        markets = [{ id: String(gid), name: String(gg.N ?? gg.SN ?? "") || ladderName(gid), selections }];
        break;
      }
    }
  }
  if (markets.length === 0) {
    const ladder = openEventMarketsFor(contextOnlyId, marketsFor);
    const winner = (ladder ? ladder.lines : []).filter((l) => l && (l.market_id === 1 || l.market_id === 702));
    if (winner.length) {
      markets = [{
        id: String(winner[0].market_id),
        name: String(winner[0].market_name ?? ""),
        selections: winner.map((l) => ({
          stake_id: String(l.stake_id), name: String(l.selection ?? ""), odds: l.odds,
          code: l.code ?? null,
        })),
      }];
    }
  }
  // A priced entry is never replaced by a priceless one: the store re-renders
  // between market loads, and the last known 1X2 beats none.
  if (markets.length === 0) {
    const kept = lastEntry.get(contextOnlyId);
    const keptMarkets = kept && Array.isArray(kept.markets) ? (kept.markets as unknown[]) : [];
    if (keptMarkets.length) markets = keptMarkets;
  }
  const entry = { ...(base as unknown as Record<string, unknown>), scored_lines, markets } as Record<string, unknown>;
  lastEntry.set(contextOnlyId, entry);
  return entry;
}
